function canonical(value) {
  if (value instanceof Date) return value.toISOString();
  if (Array.isArray(value)) return value.map(canonical);
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.keys(value).sort()
      .filter(key => value[key] !== undefined).map(key => [key, canonical(value[key])]));
  }
  return value;
}

export function recordingRequestKey(context, data) {
  return JSON.stringify(canonical({ context, data }));
}

export function createRecordingIssuanceId() {
  return globalThis.crypto?.randomUUID?.()
    || `issue-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function hash32(input, seed) {
  let hash = seed >>> 0;
  for (let index = 0; index < input.length; index += 1) {
    hash ^= input.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
}

export function deterministicDomainRecordId({ schoolId, domainId, issuedById, issuanceId } = {}) {
  if (!schoolId || !domainId || !issuedById || !issuanceId) return null;
  const identity = JSON.stringify([schoolId, domainId, issuedById, issuanceId]);
  return `event_${hash32(identity, 0x811c9dc5)}${hash32(identity, 0x9e3779b9)}`;
}

export function assertSameRecordingIdentity(saved, incoming) {
  const fields = ['schoolId', 'studentId', 'issuedById', 'issuanceId'];
  if (incoming.domainId) fields.push('domainId');
  if (fields.some(field => saved[field] !== incoming[field])) {
    const error = new Error('This recording identity belongs to another submission.');
    error.code = 'failed-precondition';
    throw error;
  }
}

/**
 * Persist identity before sending a recording request. A lost response, partial
 * batch or screen restart then retries the same intent. Storage is injected so
 * browser and native use the same ordering and failure behaviour.
 */
export function createRecordingAttemptRegistry({
  read = async () => null,
  write = async () => {},
  createId = createRecordingIssuanceId,
} = {}) {
  let attempts = null;
  let tail = Promise.resolve();
  const enqueue = operation => {
    const result = tail.then(async () => {
      if (!attempts) {
        const saved = await read();
        const parsed = saved ? JSON.parse(saved) : [];
        if (!Array.isArray(parsed) || parsed.some(entry => !Array.isArray(entry)
          || entry.length !== 2 || entry.some(value => typeof value !== 'string'))) {
          throw new Error('Saved recording attempts could not be read. Your recording has not been sent.');
        }
        attempts = new Map(parsed);
      }
      return operation();
    });
    tail = result.catch(() => {});
    return result;
  };
  return {
    prepare(context, data) {
      return enqueue(async () => {
        const key = recordingRequestKey(context, data);
        const issuanceId = attempts.get(key) || createId();
        attempts.set(key, issuanceId);
        // Retry this write even when the identity was already in memory: a
        // failed local save must not allow an unprotected server request.
        await write(JSON.stringify([...attempts]));
        return { key, issuanceId };
      });
    },
    complete(completed) {
      return enqueue(async () => {
        const next = new Map(attempts);
        for (const attempt of completed) {
          if (next.get(attempt.key) === attempt.issuanceId) next.delete(attempt.key);
        }
        await write(JSON.stringify([...next]));
        attempts = next;
      });
    },
    completeContext(context) {
      return enqueue(async () => {
        const expected = JSON.stringify(canonical(context));
        const next = new Map([...attempts].filter(([key]) =>
          JSON.stringify(JSON.parse(key).context) !== expected));
        await write(JSON.stringify([...next]));
        attempts = next;
      });
    },
  };
}

/** Clear acknowledged row answers while retaining edits made during a save. */
export function acknowledgeRecordingRows(current, snapshot, studentIds) {
  let next = current;
  for (const id of studentIds) {
    if (!Object.prototype.hasOwnProperty.call(snapshot, id)
      || current[id] !== snapshot[id]) continue;
    if (next === current) next = { ...current };
    delete next[id];
  }
  return next;
}
